// 通道组表单的草稿与换算（docs/03 4.1、docs/10 第 3 节，决策 D69）：组只设一个策略，成员是通道或另一个通道组（草稿里记成目标键，targetDraft）。
// 失败重试（retry）固定首发成员与重试成员两个位置，参数是超时与总尝试次数；按时间（time）的成员各带一段北京时间的时段；
// 主备（primary_backup）至少两个支持回执的通道、优先级不重复，参数是切走与切回阈值、小流量与验证比例、样本门槛（决策 D92）。
// 嵌套层数、环与时段覆盖全天由服务端的快照校验负责（422），这里只拦显而易见的错。
import { formatJson, intIn, parseJson, rowKey, validClockRange } from "@/app/form";
import { STRATEGY, type Channel, type ChannelGroup, type EntityStatus, type GroupMember, type Input } from "@/app/master";
import type { Errors } from "@/app/useDraft";
import { isChannelTarget, parseTarget, RETRY_ATTEMPTS_MAX, RETRY_TIMEOUT_MAX, RETRY_TIMEOUT_MIN, targetKey, type TargetNames } from "./targetDraft";

/** 通道组的策略，顺序即下拉的顺序。 */
export const STRATEGIES = ["priority", "weighted", "least_cost", "quality_based", "number_affinity", "primary_backup", "time", "retry"] as const;

/** 成员只能是通道的策略：它们要比较通道的成本与质量数据（决策 D69）。 */
export const CHANNEL_ONLY = ["least_cost", "quality_based", "number_affinity", "primary_backup"];

/** 按号码历史策略的参数范围与默认值（决策 D74），与服务端的快照校验一致：几天内送达过就优先、连续失败几次就挪后。 */
export const AFFINITY_DAYS_MAX = 30;
/** 连续失败次数的上限。 */
export const AFFINITY_FAILS_MAX = 10;
const AFFINITY_DAYS_DEFAULT = 7;
const AFFINITY_FAILS_DEFAULT = 2;

/** 主备策略的参数默认值（决策 D92），范围与服务端的快照校验一致：阈值是百分点，比例是千分比。 */
export const PB_DEFAULT = { threshold: 5, recover_threshold: 2, probe_ratio: 50, verify_ratio: 200, min_samples: 30 };
type PBParams = typeof PB_DEFAULT;

export interface MemberDraft {
  /** 只在前端用的稳定标识，做列表 key。不提交。 */
  key: number;
  /** 成员的目标键：g 加通道组 id，或 c 加通道 id；空表示还没选。 */
  target: string; priority: string; weight: string; time_start: string; time_end: string;
}
export interface ChannelGroupDraft {
  name: string; note: string; status: EntityStatus; strategy: string; members: MemberDraft[];
  /** retry 策略的参数。 */
  timeout_sec: string; max_attempts: string;
  /** number_affinity 策略的参数：几天内送达过就优先、连续失败几次就挪后（决策 D74）。 */
  recent_days: string; fail_threshold: string;
  /** primary_backup 策略的参数（决策 D92）。 */
  threshold: string; recover_threshold: string; probe_ratio: string; verify_ratio: string; min_samples: string;
  /** quality_based 的参数（按客户档位的 weights）不常用，按需添加，JSON 文本；其余策略没有参数（利润护栏已由决策 D71 去掉）。 */
  params_on: boolean; params: string;
}

export function memberDraft(m?: GroupMember): MemberDraft {
  if (!m) return { key: rowKey(), target: "", priority: "100", weight: "1", time_start: "", time_end: "" };
  return {
    key: rowKey(), target: targetKey(m.group_id, m.channel_id), priority: String(m.priority), weight: String(m.weight),
    time_start: m.time_start ?? "", time_end: m.time_end ?? "",
  };
}

/** 主备参数：服务端不给的取默认值，这里同样补上。 */
function pbParams(row?: ChannelGroup): PBParams {
  return row?.strategy === "primary_backup" ? { ...PB_DEFAULT, ...(row.params as Partial<PBParams>) } : PB_DEFAULT;
}

export function channelGroupDraft(row?: ChannelGroup): ChannelGroupDraft {
  const pb = pbParams(row);
  const pbDraft = { threshold: String(pb.threshold), recover_threshold: String(pb.recover_threshold), probe_ratio: String(pb.probe_ratio), verify_ratio: String(pb.verify_ratio), min_samples: String(pb.min_samples) };
  if (!row) return { name: "", note: "", status: "active", strategy: "priority", members: [memberDraft()], timeout_sec: "60", max_attempts: "2", recent_days: String(AFFINITY_DAYS_DEFAULT), fail_threshold: String(AFFINITY_FAILS_DEFAULT), ...pbDraft, params_on: false, params: "{}" };
  const params = (row.params ?? {}) as Record<string, unknown>;
  const retry = row.strategy === "retry";
  const affinity = row.strategy === "number_affinity";
  return {
    name: row.name, note: row.note ?? "", status: row.status, strategy: row.strategy, members: row.members.map(memberDraft), ...pbDraft,
    timeout_sec: retry ? String(params.timeout_sec ?? 60) : "60", max_attempts: retry ? String(params.max_attempts ?? 2) : "2",
    recent_days: String((affinity && params.recent_days) || AFFINITY_DAYS_DEFAULT), fail_threshold: String((affinity && params.fail_threshold) || AFFINITY_FAILS_DEFAULT),
    params_on: row.strategy === "quality_based" && Object.keys(params).length > 0, params: row.strategy === "quality_based" ? formatJson(params) : "{}",
  };
}

/** 添加一个成员：主备的优先级不能重复，接在现有最大值之后，其余策略沿用默认值。 */
export function newMember(d: ChannelGroupDraft): MemberDraft {
  const m = memberDraft();
  if (d.strategy !== "primary_backup") return m;
  const max = Math.max(0, ...d.members.map((x) => Number(x.priority) || 0));
  return { ...m, priority: String(max + 1) };
}

/** 换策略时整理成员：retry 固定两个位置，多的丢掉、少的补空；换到主备时优先级有重复，就按现在的优先级（按数值，相同的保持行序）排好再改成 1、2、3，
 *  原来最先选的成员仍是主通道（决策 D92："priority 最小的是主通道"）。 */
export function withStrategy(d: ChannelGroupDraft, strategy: string): ChannelGroupDraft {
  if (strategy === "primary_backup" && new Set(d.members.map((m) => Number(m.priority))).size !== d.members.length) {
    const sorted = [...d.members].sort((a, b) => (Number(a.priority) || 0) - (Number(b.priority) || 0));
    return { ...d, strategy, members: sorted.map((m, i) => ({ ...m, priority: String(i + 1) })) };
  }
  if (strategy !== "retry") return { ...d, strategy };
  const members = [...d.members.slice(0, 2)];
  while (members.length < 2) members.push(memberDraft());
  return { ...d, strategy, members };
}

const nonNegative = (s: string) => /^\d+$/.test(s.trim());

/** 校验一个成员，返回错误文案或换算后的成员。retry 的两个位置按顺序给 priority 1、2。 */
function memberInput(d: ChannelGroupDraft, m: MemberDraft, index: number): string | GroupMember {
  if (!m.target) return d.strategy === "retry" ? `选择${index === 0 ? "首发" : "重试"}成员。` : "选择成员。";
  if (CHANNEL_ONLY.includes(d.strategy) && !isChannelTarget(m.target)) return `${STRATEGY[d.strategy]}的成员只能是通道。`;
  if (!nonNegative(m.priority) || !nonNegative(m.weight)) return "优先级与权重是非负整数。";
  const time = d.strategy === "time";
  if (time && !validClockRange(m.time_start, m.time_end)) return "时段是 时:分，例如 08:00，起止不能相同。";
  const t = parseTarget(m.target);
  return {
    channel_id: t.channel, group_id: t.group, priority: d.strategy === "retry" ? index + 1 : Number(m.priority), weight: Number(m.weight),
    time_start: time ? m.time_start : null, time_end: time ? m.time_end : null,
  };
}

/** 策略参数：retry 取超时与次数，number_affinity 取天数与次数，time 没有参数，quality_based 取按需添加的 JSON。错误记在对应的字段上。 */
function paramsInput(d: ChannelGroupDraft, errors: Errors<ChannelGroupDraft>): object {
  if (d.strategy === "retry") {
    const timeout = intIn(d.timeout_sec, RETRY_TIMEOUT_MIN, RETRY_TIMEOUT_MAX), attempts = intIn(d.max_attempts, 2, RETRY_ATTEMPTS_MAX);
    if (timeout === null) errors.timeout_sec = `取 ${RETRY_TIMEOUT_MIN} 到 ${RETRY_TIMEOUT_MAX} 秒。`;
    if (attempts === null) errors.max_attempts = `取 2 到 ${RETRY_ATTEMPTS_MAX}。`;
    return { timeout_sec: timeout, max_attempts: attempts };
  }
  if (d.strategy === "number_affinity") {
    const days = intIn(d.recent_days, 1, AFFINITY_DAYS_MAX), fails = intIn(d.fail_threshold, 1, AFFINITY_FAILS_MAX);
    if (days === null) errors.recent_days = `取 1 到 ${AFFINITY_DAYS_MAX} 天。`;
    if (fails === null) errors.fail_threshold = `取 1 到 ${AFFINITY_FAILS_MAX} 次。`;
    return { recent_days: days, fail_threshold: fails };
  }
  if (d.strategy === "primary_backup") return pbInput(d, errors);
  if (d.strategy !== "quality_based" || !d.params_on) return {};
  const p = parseJson(d.params, {});
  const err = qualityParamsError(p);
  if (!err) return p as object;
  errors.params = err;
  return {};
}

const isObject = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === "object" && !Array.isArray(v);
const WEIGHT_KEYS = ["quality", "cost", "latency"];

/** 按质量策略的参数，与服务端的保存校验一致（决策 D101）：只认 weights；weights 按档位，每档只认 quality、cost、latency 三个数字，
 *  缺省按 0，都不小于 0 且和大于 0。档位名不限，没写的档位用默认权重。 */
function qualityParamsError(p: unknown): string | undefined {
  if (!isObject(p)) return "策略参数是 JSON 对象。";
  const extra = Object.keys(p).find((k) => k !== "weights");
  if (extra) return `不认识的参数 ${extra}，只能有 weights。`;
  if (p.weights == null) return;
  if (!isObject(p.weights)) return "weights 是按客户档位的对象。";
  for (const [tier, w] of Object.entries(p.weights)) {
    if (!isObject(w)) return `档位 ${tier} 的权重是对象，例如 {"quality": 0.5, "cost": 0.4, "latency": 0.1}。`;
    const bad = Object.keys(w).find((k) => !WEIGHT_KEYS.includes(k));
    if (bad) return `档位 ${tier}：不认识的权重 ${bad}，只能是 quality、cost、latency。`;
    const values = WEIGHT_KEYS.map((k) => w[k] === undefined ? 0 : w[k]);
    if (values.some((v) => typeof v !== "number" || !Number.isFinite(v) || v < 0)) return `档位 ${tier}：权重是不小于 0 的数字。`;
    const total = (values as number[]).reduce((a, b) => a + b, 0);
    if (!Number.isFinite(total)) return `档位 ${tier}：三项权重之和必须是有限的正数。`;
    if (total <= 0) return `档位 ${tier}：三项权重不能都是 0。`;
  }
}

/** 主备参数（决策 D92）：默认值同样参与参数之间的校验，切回阈值小于切走阈值、验证比例大于小流量比例。 */
function pbInput(d: ChannelGroupDraft, errors: Errors<ChannelGroupDraft>): PBParams {
  const threshold = intIn(d.threshold, 1, 50), recover = intIn(d.recover_threshold, 0, 49), probe = intIn(d.probe_ratio, 1, 200), verify = intIn(d.verify_ratio, 1, 500), min = intIn(d.min_samples, 10, 1000);
  if (threshold === null) errors.threshold = "取 1 到 50 个百分点。";
  if (recover === null || (threshold !== null && recover >= threshold)) errors.recover_threshold = "取 0 到切走阈值减 1。";
  if (probe === null) errors.probe_ratio = "取 1 到 200（千分比）。";
  if (verify === null || (probe !== null && verify <= probe)) errors.verify_ratio = "大于小流量比例，不超过 500（千分比）。";
  if (min === null) errors.min_samples = "取 10 到 1000。";
  return { threshold: threshold ?? 0, recover_threshold: recover ?? 0, probe_ratio: probe ?? 0, verify_ratio: verify ?? 0, min_samples: min ?? 0 };
}

/** 主备的成员（决策 D92）：至少两个、优先级不重复、都支持回执（不支持的没有送达样本，D54）。channels 没加载时不查回执，交给服务端。 */
function pbMembersError(members: GroupMember[], channels?: Channel[]): string | undefined {
  if (members.length < 2) return "主备至少两个成员：优先级最小的是主通道，其余是备用。";
  if (new Set(members.map((m) => m.priority)).size !== members.length) return "主备的成员优先级不能重复。";
  const noDLR = channels?.find((c) => !c.dlr_supported && members.some((m) => m.channel_id === c.id));
  if (noDLR) return `主备的成员必须支持回执，${noDLR.name} 不支持。`;
}

/** 换算整组。groups 是全部通道组，用来检查重试成员不包含失败重试的组（与服务端的快照校验一致，决策 D69）；channels 用来检查主备的成员支持回执。 */
export function channelGroupInput(d: ChannelGroupDraft, groups?: ChannelGroup[], channels?: Channel[]): { body?: Input<ChannelGroup>; errors: Errors<ChannelGroupDraft> } {
  const errors: Errors<ChannelGroupDraft> = {};
  if (!d.name.trim()) errors.name = "填写通道组合名称。";
  const params = paramsInput(d, errors);
  const members: GroupMember[] = [];
  const seen = new Set<string>();
  for (const [i, m] of d.members.entries()) {
    if (m.target && seen.has(m.target)) { errors.members = "同一个成员只能出现一次。"; break; }
    seen.add(m.target);
    const r = memberInput(d, m, i);
    if (typeof r === "string") { errors.members = d.members.length > 1 && d.strategy !== "retry" ? `第 ${i + 1} 个成员：${r}` : r; break; }
    members.push(r);
  }
  if (!errors.members && !members.length) errors.members = "至少一个成员。";
  const retryGroup = d.strategy === "retry" && members.length === 2 ? members[1].group_id : null;
  if (!errors.members && retryGroup !== null && containsRetry(retryGroup, groups)) errors.members = "重试成员不能是、也不能包含失败重试的通道组合。";
  const pbError = !errors.members && d.strategy === "primary_backup" ? pbMembersError(members, channels) : undefined;
  if (pbError) errors.members = pbError;
  if (Object.keys(errors).length) return { errors };
  return { errors, body: { name: d.name.trim(), note: d.note.trim() || null, status: d.status, strategy: d.strategy, params, members } };
}

/** 成员的展示名：通道直接写名字，组写成"组 名字"。组的摘要里一律用它，看得出成员是通道还是组。 */
function memberName(m: GroupMember, names: TargetNames): string {
  return m.channel_id ? names.channel(m.channel_id) : `组 ${names.group(m.group_id ?? 0)}`;
}

/** 组的策略摘要，列表页与通道组编辑的提示共用：retry 写出首发、重试成员与参数，number_affinity 写出门槛，primary_backup 写出主通道与阈值，time 写出各时段，其余写策略名。 */
export function strategySummary(g: ChannelGroup, names: TargetNames): string {
  const members = byPriority(g);
  if (g.strategy === "retry" && members.length === 2) {
    const p = (g.params ?? {}) as { timeout_sec?: number; max_attempts?: number };
    return `首发 ${memberName(members[0], names)}，失败重试到 ${memberName(members[1], names)}，${p.timeout_sec} 秒，共 ${p.max_attempts} 次`;
  }
  if (g.strategy === "number_affinity") {
    const p = (g.params ?? {}) as { recent_days?: number; fail_threshold?: number };
    return `按号码历史：${p.recent_days || AFFINITY_DAYS_DEFAULT} 天内送达过的通道优先，连续失败 ${p.fail_threshold || AFFINITY_FAILS_DEFAULT} 次的挪后`;
  }
  if (g.strategy === "primary_backup" && members.length) {
    const p = pbParams(g);
    return `主备：主通道 ${memberName(members[0], names)}，送达率比备用低 ${p.threshold} 个百分点以上就切过去，小流量 ${p.probe_ratio / 10}%`;
  }
  if (g.strategy === "time") {
    return [...g.members].sort((a, b) => (a.time_start ?? "").localeCompare(b.time_start ?? "")).map((m) => `${m.time_start}–${m.time_end} ${memberName(m, names)}`).join("；");
  }
  return STRATEGY[g.strategy] ?? g.strategy;
}

/** 成员摘要：按优先级列出成员名，列表页与通道组编辑的说明共用。 */
export function membersSummary(g: ChannelGroup, names: TargetNames): string {
  return byPriority(g).map((m) => memberName(m, names)).join("、") || "—";
}

/** 成员按优先级从小到大排，不改原数组。 */
function byPriority(g: ChannelGroup): GroupMember[] {
  return [...g.members].sort((a, b) => a.priority - b.priority);
}

/** 组 id 是不是、或者往下有没有 retry 组：重试成员里不能有（决策 D69）。已经看过的组不再看，有环也能结束。 */
export function containsRetry(id: number, groups: ChannelGroup[] | undefined, seen = new Set<number>()): boolean {
  if (seen.has(id)) return false;
  seen.add(id);
  const g = groups?.find((x) => x.id === id);
  return !!g && (g.strategy === "retry" || g.members.some((m) => m.group_id !== null && containsRetry(m.group_id, groups, seen)));
}
